/**
 * Image asset guide for editors: exact filenames, folders under src/assets/img/
 * Dimensions are recommended export sizes (use @2x source files where noted).
 */

export const imageAssetGuideIntro = {
  title: "PCF image asset guide",
  basePath: "Jagowelfare/src/assets/img/",
  notes: [
    "Keep the filename and extension exactly as listed so imports keep working.",
    "Paths are relative to src/assets/img/ unless noted as public/.",
    "For photos of people, use images you have rights to (or AI-generated stock) and compress for web (e.g. Squoosh).",
  ],
};

/**
 * @typedef {{ file: string, subfolder: string, dims: string, format?: string, purpose: string }} AssetRow
 * @typedef {{ id: string, title: string, route: string, intro?: string, assets: AssetRow[] }} GuidePage
 */

/** @type {GuidePage[]} */
export const imageAssetGuidePages = [
  {
    id: "site-wide",
    title: "Site-wide (header, footer, inner banners)",
    route: "All pages",
    intro: "Logo and UI chrome used across the site.",
    assets: [
      {
        file: "logo.png",
        subfolder: "",
        dims: "560 × 160 px (or SVG equivalent)",
        format: "PNG / SVG",
        purpose: "PCF wordmark in header + footer. Transparent background recommended.",
      },
      {
        file: "search_icon.png",
        subfolder: "icon",
        dims: "48 × 48 px",
        purpose: "Search control in navbar (decorative).",
      },
      {
        file: "arrow.png",
        subfolder: "icon",
        dims: "24 × 24 px",
        purpose: "Breadcrumb arrow on all inner page banners (Common banner).",
      },
    ],
  },
  {
    id: "home",
    title: "Home",
    route: "/",
    assets: [
      {
        file: "banner.png",
        subfolder: "banner",
        dims: "1600 × 1200 px (hero, ~4:3; safe crop center-weighted)",
        purpose: "Main hero image beside headline.",
      },
      {
        file: "element-1.png",
        subfolder: "banner",
        dims: "320 × 320 px",
        format: "PNG transparent",
        purpose: "Decorative floating graphic (shape 1).",
      },
      {
        file: "element-2.png",
        subfolder: "banner",
        dims: "320 × 320 px",
        format: "PNG transparent",
        purpose: "Decorative floating graphic (shape 2).",
      },
      {
        file: "element-3.png",
        subfolder: "banner",
        dims: "320 × 320 px",
        format: "PNG transparent",
        purpose: "Decorative floating graphic (shape 3).",
      },
      {
        file: "book.png",
        subfolder: "icon",
        dims: "96 × 96 px",
        purpose: "About-top tile: education.",
      },
      {
        file: "paint.png",
        subfolder: "icon",
        dims: "96 × 96 px",
        purpose: "About-top tile: culture.",
      },
      {
        file: "heart.png",
        subfolder: "icon",
        dims: "96 × 96 px",
        purpose: "About-top tile: sport / badminton focus.",
      },
      {
        file: "restaurant.png",
        subfolder: "icon",
        dims: "96 × 96 px",
        purpose: "About-top tile: integration / community.",
      },
      {
        file: "round.png",
        subfolder: "icon",
        dims: "120 × 120 px",
        format: "PNG transparent",
        purpose: "Vector badge behind about-top icon clusters.",
      },
      {
        file: "about.png",
        subfolder: "common",
        dims: "1000 × 800 px",
        purpose: "Large image in home “Welcome to PCF” column.",
      },
      {
        file: "about.png",
        subfolder: "icon",
        dims: "64 × 64 px",
        purpose: "Small accent icon next to section heading (same filename in icon/).",
      },
      {
        file: "causes-1.png",
        subfolder: "causes",
        dims: "800 × 560 px",
        purpose: "Home “Our programs” card 1 (also Programs list).",
      },
      {
        file: "causes-2.png",
        subfolder: "causes",
        dims: "800 × 560 px",
        purpose: "Home programs card 2.",
      },
      {
        file: "causes-3.png",
        subfolder: "causes",
        dims: "800 × 560 px",
        purpose: "Home programs card 3.",
      },
      {
        file: "event-big.png",
        subfolder: "event",
        dims: "1000 × 700 px",
        purpose: "Featured upcoming event image (left column).",
      },
      {
        file: "clock.png",
        subfolder: "icon",
        dims: "48 × 48 px",
        purpose: "Event meta: time icon.",
      },
      {
        file: "map.png",
        subfolder: "icon",
        dims: "48 × 48 px",
        purpose: "Event meta: location icon.",
      },
      {
        file: "date.png",
        subfolder: "icon",
        dims: "48 × 48 px",
        purpose: "Event meta: date icon.",
      },
      {
        file: "logo-1.png … logo-7.png",
        subfolder: "partner",
        dims: "220 × 100 px each (consistent height)",
        purpose: "Partner / sponsor strip carousel (7 placeholders).",
      },
      {
        file: "camp2.png",
        subfolder: "icon",
        dims: "64 × 64 px",
        purpose: "Stats counter icon 1.",
      },
      {
        file: "hand.png",
        subfolder: "icon",
        dims: "64 × 64 px",
        purpose: "Stats counter icon 2.",
      },
      {
        file: "mone.png",
        subfolder: "icon",
        dims: "64 × 64 px",
        purpose: "Stats counter icon 3.",
      },
      {
        file: "camp.png",
        subfolder: "icon",
        dims: "64 × 64 px",
        purpose: "Stats counter icon 4.",
      },
      {
        file: "blog-1.png",
        subfolder: "blog",
        dims: "800 × 540 px",
        purpose: "News card image 1 on home.",
      },
      {
        file: "blog-2.png",
        subfolder: "blog",
        dims: "800 × 540 px",
        purpose: "News card image 2 on home.",
      },
      {
        file: "blog-3.png",
        subfolder: "blog",
        dims: "800 × 540 px",
        purpose: "News card image 3 on home.",
      },
      {
        file: "user.png",
        subfolder: "icon",
        dims: "40 × 40 px",
        purpose: "News card “author” meta icon.",
      },
      {
        file: "cal.png",
        subfolder: "icon",
        dims: "40 × 40 px",
        purpose: "News card date meta icon.",
      },
    ],
  },
  {
    id: "about",
    title: "About",
    route: "/about",
    assets: [
      {
        file: "about.png",
        subfolder: "common",
        dims: "1000 × 800 px",
        purpose: "Main about column photo (same as home About section).",
      },
      {
        file: "about.png",
        subfolder: "icon",
        dims: "64 × 64 px",
        purpose: "Section heading glyph.",
      },
      {
        file: "volunteer-1.png … volunteer-4.png",
        subfolder: "volunteer",
        dims: "400 × 400 px (square head-and-shoulders)",
        purpose: "“Our volunteers” grid on About (4 cards). Use consistent lighting.",
      },
    ],
  },
  {
    id: "programs",
    title: "Programs (listing)",
    route: "/causes",
    assets: [
      {
        file: "causes-1.png … causes-6.png",
        subfolder: "causes",
        dims: "800 × 560 px each",
        purpose: "Six program cards on /causes (education, badminton, culture, integration, seasonal sports, social).",
      },
      {
        file: "cal.png",
        subfolder: "icon",
        dims: "40 × 40 px",
        purpose: "Card footer meta: season/date.",
      },
      {
        file: "user.png",
        subfolder: "icon",
        dims: "40 × 40 px",
        purpose: "Card footer meta: lead/volunteer.",
      },
    ],
  },
  {
    id: "program-details",
    title: "Program details",
    route: "/cause-details",
    assets: [
      {
        file: "details-big.png",
        subfolder: "causes",
        dims: "1200 × 720 px",
        purpose: "Top hero image for detail article.",
      },
      {
        file: "details-small-1.png",
        subfolder: "causes",
        dims: "600 × 400 px",
        purpose: "Inline gallery thumb 1.",
      },
      {
        file: "details-small-2.png",
        subfolder: "causes",
        dims: "600 × 400 px",
        purpose: "Inline gallery thumb 2.",
      },
      {
        file: "pdf.png",
        subfolder: "icon",
        dims: "48 × 48 px",
        purpose: "Download row icon.",
      },
      {
        file: "post-1.png",
        subfolder: "common",
        dims: "96 × 96 px",
        purpose: "Comment avatar placeholder 1.",
      },
      {
        file: "post-2.png",
        subfolder: "common",
        dims: "96 × 96 px",
        purpose: "Comment avatar placeholder 2.",
      },
      {
        file: "project_organizer.png",
        subfolder: "sidebar",
        dims: "200 × 200 px",
        purpose: "Sidebar “program lead” portrait.",
      },
      {
        file: "rec-donet-1.png … rec-donet-5.png",
        subfolder: "sidebar",
        dims: "120 × 80 px",
        purpose: "Sidebar “Community supporters” thumbnails.",
      },
      {
        file: "rec-cas-1.png … rec-cas-4.png",
        subfolder: "sidebar",
        dims: "120 × 80 px",
        purpose: "Sidebar “Related programs” thumbs (reused on News/Support sidebars).",
      },
    ],
  },
  {
    id: "events",
    title: "Events",
    route: "/event",
    assets: [
      {
        file: "event-big.png",
        subfolder: "event",
        dims: "1000 × 700 px",
        purpose: "Featured event hero (same file as home events block).",
      },
      {
        file: "clock.png, map.png, date.png",
        subfolder: "icon",
        dims: "48 × 48 px",
        purpose: "Event listing meta icons.",
      },
    ],
  },
  {
    id: "event-details",
    title: "Event details",
    route: "/event-details",
    assets: [
      {
        file: "details-big.png, details-small-1.png, details-small-2.png",
        subfolder: "causes",
        dims: "Same as program details",
        purpose: "Event detail shares the same image filenames as cause details template.",
      },
      {
        file: "rec-donet-1.png … rec-donet-3.png",
        subfolder: "sidebar",
        dims: "120 × 80 px",
        purpose: "Sidebar organizer mini-cards.",
      },
      {
        file: "email.png, phone.png",
        subfolder: "icon",
        dims: "40 × 40 px",
        purpose: "Contact lines in event sidebar.",
      },
    ],
  },
  {
    id: "news",
    title: "News (listing)",
    route: "/news",
    assets: [
      {
        file: "blog-1.png, blog-2.png, blog-3.png",
        subfolder: "blog",
        dims: "800 × 540 px",
        purpose: "Repeated across listing rows (6 stories use same three files in rotation in data).",
      },
    ],
  },
  {
    id: "news-details",
    title: "News details",
    route: "/news-details",
    assets: [
      {
        file: "details-big.png, details-small-1.png, details-small-2.png",
        subfolder: "causes",
        dims: "1200 × 720 / 600 × 400",
        purpose: "Article hero + inline images.",
      },
      {
        file: "project_organizer.png, rec-cas-1…4",
        subfolder: "sidebar",
        dims: "As program details",
        purpose: "News sidebar thumbnails.",
      },
    ],
  },
  {
    id: "gallery",
    title: "Gallery",
    route: "/gallery",
    assets: [
      {
        file: "gallery-grid-1.png … gallery-grid-6.png",
        subfolder: "gallery",
        dims: "900 × 900 px (square grid)",
        purpose: "Six masonry/grid tiles.",
      },
      {
        file: "arrow-round.png",
        subfolder: "icon",
        dims: "48 × 48 px",
        purpose: "Gallery control / overlay icon.",
      },
    ],
  },
  {
    id: "team",
    title: "Our team",
    route: "/team",
    assets: [
      {
        file: "volunteer-1.png … volunteer-8.png",
        subfolder: "volunteer",
        dims: "400 × 400 px",
        purpose: "Eight team portraits (full grid).",
      },
    ],
  },
  {
    id: "testimonials",
    title: "Testimonials",
    route: "/testimonial",
    assets: [
      {
        file: "test-1.png",
        subfolder: "testimonial",
        dims: "160 × 160 px",
        purpose: "Carousel card photo (same file repeated in slider).",
      },
      {
        file: "quot.png",
        subfolder: "common",
        dims: "64 × 64 px",
        format: "PNG transparent",
        purpose: "Quote mark accent.",
      },
    ],
  },
  {
    id: "support",
    title: "Support PCF",
    route: "/make-donation",
    assets: [
      {
        file: "project_organizer.png, rec-cas-1…4",
        subfolder: "sidebar",
        dims: "As above",
        purpose: "Sidebar visuals.",
      },
      {
        file: "visa.png",
        subfolder: "public/assets/img/icon",
        dims: "64 × 40 px",
        format: "PNG",
        purpose: "Payment logos row (referenced as assets/img/... from public/). Save under public/assets/img/icon/.",
      },
      {
        file: "skill.png",
        subfolder: "public/assets/img/icon",
        dims: "64 × 40 px",
        purpose: "Payment logos — same folder as visa.",
      },
      {
        file: "master.png",
        subfolder: "public/assets/img/icon",
        dims: "64 × 40 px",
        purpose: "Payment logos — same folder as visa.",
      },
      {
        file: "paypal.png",
        subfolder: "public/assets/img/icon",
        dims: "64 × 40 px",
        purpose: "Payment logos — same folder as visa.",
      },
    ],
  },
  {
    id: "faqs",
    title: "FAQ",
    route: "/faqs",
    assets: [
      {
        file: "money.png, setting.png, hand-round.png, round.png",
        subfolder: "icon",
        dims: "80–120 px",
        purpose: "Three FAQ highlight tiles + decorative round vector.",
      },
    ],
  },
  {
    id: "contact",
    title: "Contact",
    route: "/contact",
    assets: [
      {
        file: "sm-location.png, email-color.png, phon-color.png",
        subfolder: "icon",
        dims: "56 × 56 px",
        purpose: "Contact method icons.",
      },
    ],
  },
  {
    id: "error",
    title: "404 error page",
    route: "*",
    assets: [
      {
        file: "error.png",
        subfolder: "common",
        dims: "800 × 520 px",
        purpose: "Friendly error illustration.",
      },
    ],
  },
];
